import { sql } from "drizzle-orm";
import {
  claimDueWorkflowV2Outbox,
  enqueueMissingQueuedWorkflowV2Outbox,
  fireDueWorkflowV2Timers,
  markWorkflowV2OutboxSent,
} from "./workflow-kernel-v2-durable.js";
import {
  collectWorkflowV2ErrorCodes,
  collectWorkflowV2ErrorMessages,
  executeWorkflowV2Query,
} from "./workflow-kernel-v2-db.js";
import { getWorkflowV2Run, type WorkflowV2DbLike } from "./workflow-kernel-v2-store.js";
import { mirrorWorkflowV2RunFailed, mirrorWorkflowV2RunPending } from "./workflow-kernel-v2-read-model.js";
import {
  processWorkflowV2ExpiredDeadlines,
  terminalizeRequestedWorkflowV2Cancellations,
} from "./workflow-kernel-v2-terminalizers.js";
import { getWorkflowResumeDeliveryJobId } from "./workflow-resume-args.js";
import { createSingleFlightWake } from "./wake-signal/single-flight-wake.js";
import type { WakeSignalBus } from "./wake-signal/postgres-wake-signal-bus.js";
import type { WakeSignalKind } from "./wake-signal/wake-signal-types.js";

type SchedulerLike = {
  runAfter(delayMs: number, actionName: string, args: unknown, options?: { id?: string }): string;
};

type WorkflowLike = {
  name: string;
};

type LoggerLike = Pick<Console, "log" | "warn" | "error">;
type TimerHandle = ReturnType<typeof setTimeout>;

export const WORKFLOW_V2_RECONCILER_INTERVAL_MS = 5_000;
export const WORKFLOW_V2_RECONCILER_MIN_INTERVAL_MS = 1_000;
export const WORKFLOW_V2_RECONCILER_MAX_INTERVAL_MS = 300_000;
export const WORKFLOW_V2_RECONCILER_INTERVAL_ENV = "GENCOW_WORKFLOW_RECONCILER_INTERVAL_MS";

export type WorkflowV2ReconcileResult = {
  cancelled: number;
  deadlinesRequeued: number;
  deadlinesFailed: number;
  timersFired: number;
  orphanWakeRepaired: number;
  wakeSignalsSent: number;
};

const EMPTY_WORKFLOW_V2_RECONCILE_RESULT: WorkflowV2ReconcileResult = {
  cancelled: 0,
  deadlinesRequeued: 0,
  deadlinesFailed: 0,
  timersFired: 0,
  orphanWakeRepaired: 0,
  wakeSignalsSent: 0,
};

function isMissingWorkflowV2SchemaError(error: unknown): boolean {
  const codes = new Set(collectWorkflowV2ErrorCodes(error));
  const message = collectWorkflowV2ErrorMessages(error).join("\n");
  return (
    codes.has("42P01") ||
    (codes.has("42703") && message.includes("_gencow_workflow")) ||
    message.includes("_gencow_workflow_runs_v2") ||
    message.includes("_gencow_workflow_timers_v2") ||
    message.includes("_gencow_workflow_outbox_v2")
  );
}

export async function reconcileWorkflowV2(options: {
  db: WorkflowV2DbLike;
  workflowNames: Iterable<string>;
  scheduleResume: (workflowName: string, runId: string, delayMs: number, outboxId: string) => string;
  limit?: number;
}): Promise<WorkflowV2ReconcileResult> {
  const allowedNames = new Set(options.workflowNames);
  if (allowedNames.size === 0) {
    return {
      cancelled: 0,
      deadlinesRequeued: 0,
      deadlinesFailed: 0,
      timersFired: 0,
      orphanWakeRepaired: 0,
      wakeSignalsSent: 0,
    };
  }

  try {
    const cancelledRuns = (
      await terminalizeRequestedWorkflowV2Cancellations(options.db, options.limit ?? 100)
    ).filter((run) => allowedNames.has(run.workflow_name));
    const deadlineResult = await processWorkflowV2ExpiredDeadlines(options.db, options.limit ?? 100);
    const deadlineRequeues = deadlineResult.requeued.filter((run) => allowedNames.has(run.workflow_name));
    const deadlineFailures = deadlineResult.failed.filter((run) => allowedNames.has(run.workflow_name));
    const firedTimers = await fireDueWorkflowV2Timers(options.db, options.limit ?? 100);
    const repairedOutbox = await enqueueMissingQueuedWorkflowV2Outbox({
      db: options.db,
      workflowNames: allowedNames,
      limit: options.limit ?? 100,
    });
    const dueOutbox = await claimDueWorkflowV2Outbox(options.db, options.limit ?? 100, allowedNames);
    let wakeSignalsSent = 0;

    for (const run of deadlineRequeues) {
      await mirrorWorkflowV2RunPending({
        db: options.db,
        runId: run.id,
        stepKey: run.current_step_key,
        retryCount: run.retry_count,
        error: run.error_json,
        errorCode: run.error_code,
      });
    }

    for (const run of [...cancelledRuns, ...deadlineFailures]) {
      await mirrorWorkflowV2RunFailed({
        db: options.db,
        runId: run.id,
        retryCount: run.retry_count,
        error: run.error_json,
        errorCode: run.error_code,
      });
    }

    for (const outbox of dueOutbox) {
      const run = await getWorkflowV2Run(options.db, outbox.run_id);
      if (!run || !allowedNames.has(run.workflow_name)) continue;

      options.scheduleResume(run.workflow_name, run.id, 0, outbox.id);
      await markWorkflowV2OutboxSent(options.db, outbox.id);
      wakeSignalsSent += 1;
    }

    return {
      cancelled: cancelledRuns.length,
      deadlinesRequeued: deadlineRequeues.length,
      deadlinesFailed: deadlineFailures.length,
      timersFired: firedTimers.length,
      orphanWakeRepaired: repairedOutbox.length,
      wakeSignalsSent,
    };
  } catch (error) {
    if (isMissingWorkflowV2SchemaError(error)) {
      return EMPTY_WORKFLOW_V2_RECONCILE_RESULT;
    }
    throw error;
  }
}

function workflowV2ErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function clampWorkflowV2ReconcilerIntervalMs(value: number): number {
  return Math.min(
    WORKFLOW_V2_RECONCILER_MAX_INTERVAL_MS,
    Math.max(WORKFLOW_V2_RECONCILER_MIN_INTERVAL_MS, value),
  );
}

export function resolveWorkflowV2ReconcilerIntervalMs(
  env: Record<string, string | undefined> | undefined,
  override?: number,
): number {
  if (typeof override === "number" && Number.isFinite(override) && override > 0) return override;

  const rawValue = env?.[WORKFLOW_V2_RECONCILER_INTERVAL_ENV];
  if (!rawValue) return WORKFLOW_V2_RECONCILER_INTERVAL_MS;

  const parsedValue = Number(rawValue);
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) return WORKFLOW_V2_RECONCILER_INTERVAL_MS;
  return clampWorkflowV2ReconcilerIntervalMs(parsedValue);
}

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function timestampMs(value: unknown): number | null {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === "string" || typeof value === "number") {
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

async function getNextWorkflowV2WakeDueAtMs(options: {
  db: WorkflowV2DbLike;
  workflowNames: Iterable<string>;
}): Promise<number | null> {
  const workflowNames = [...new Set(options.workflowNames)];
  if (workflowNames.length === 0) return null;

  const result = await executeWorkflowV2Query(
    options.db,
    sql`
      WITH next_candidates AS (
        SELECT outbox.available_at AS due_at
        FROM _gencow_workflow_outbox_v2 outbox
        JOIN _gencow_workflow_runs_v2 run ON run.id = outbox.run_id
        WHERE outbox.status = 'pending'
          AND outbox.available_at > NOW()
          AND run.workflow_name IN (${sql.join(
            workflowNames.map((name) => sql`${name}`),
            sql`, `,
          )})

        UNION ALL

        SELECT timer.fire_at AS due_at
        FROM _gencow_workflow_timers_v2 timer
        JOIN _gencow_workflow_runs_v2 run ON run.id = timer.run_id
        WHERE timer.status = 'pending'
          AND timer.fire_at > NOW()
          AND run.workflow_name IN (${sql.join(
            workflowNames.map((name) => sql`${name}`),
            sql`, `,
          )})
      )
      SELECT MIN(due_at) AS next_due_at
      FROM next_candidates
    `,
  );
  return timestampMs(rowsFromResult<{ next_due_at: unknown }>(result)[0]?.next_due_at);
}

export function startWorkflowV2Reconciler(params: {
  db: WorkflowV2DbLike;
  workflows: WorkflowLike[];
  scheduler: SchedulerLike;
  getWorkflowResumeActionName: (workflowName: string) => string;
  logger?: LoggerLike;
  intervalMs?: number;
  setIntervalImpl?: typeof setInterval;
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
  now?: () => Date;
  wakeSignalBus?: WakeSignalBus;
  wakeSignalKinds?: readonly WakeSignalKind[];
}): void {
  if (params.workflows.length === 0) return;
  const logger = params.logger ?? console;
  const intervalMs = resolveWorkflowV2ReconcilerIntervalMs(process.env, params.intervalMs);
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const setTimeoutImpl = params.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = params.clearTimeoutImpl ?? clearTimeout;
  const now = params.now ?? (() => new Date());
  const workflowNames = params.workflows.map((workflow) => workflow.name);
  let nextDueTimer: TimerHandle | null = null;

  function clearNextDueTimer(): void {
    if (!nextDueTimer) return;
    clearTimeoutImpl(nextDueTimer);
    nextDueTimer = null;
  }

  let tick: () => Promise<WorkflowV2ReconcileResult>;

  async function scheduleNextDueTimer(): Promise<void> {
    if (!params.wakeSignalBus) return;

    const nextDueAtMs = await getNextWorkflowV2WakeDueAtMs({
      db: params.db,
      workflowNames,
    }).catch((error: unknown) => {
      if (isMissingWorkflowV2SchemaError(error)) return null;
      logger.warn(`[workflow:v2] next wake lookup failed: ${workflowV2ErrorMessage(error)}`);
      return null;
    });
    if (nextDueAtMs === null) {
      clearNextDueTimer();
      return;
    }

    const delayMs = nextDueAtMs - now().getTime();
    if (delayMs < 0 || delayMs >= intervalMs) {
      clearNextDueTimer();
      return;
    }

    clearNextDueTimer();
    nextDueTimer = setTimeoutImpl(() => {
      nextDueTimer = null;
      tick().catch((error: unknown) => {
        logger.error(`[workflow:v2] next-due timer failed: ${workflowV2ErrorMessage(error)}`);
      });
    }, delayMs);
    if (typeof (nextDueTimer as { unref?: () => void }).unref === "function") {
      (nextDueTimer as { unref: () => void }).unref();
    }
  }

  tick = createSingleFlightWake(async () => {
    clearNextDueTimer();
    try {
      const result = await reconcileWorkflowV2({
        db: params.db,
        workflowNames,
        scheduleResume: (workflowName, runId, delayMs) =>
          params.scheduler.runAfter(
            delayMs,
            params.getWorkflowResumeActionName(workflowName),
            { workflowId: runId },
            {
              id: getWorkflowResumeDeliveryJobId(runId),
            },
          ),
      });
      if (
        result.cancelled > 0 ||
        result.deadlinesRequeued > 0 ||
        result.deadlinesFailed > 0 ||
        result.timersFired > 0 ||
        result.orphanWakeRepaired > 0 ||
        result.wakeSignalsSent > 0
      ) {
        logger.log(
          `[workflow:v2] reconciled cancelled=${result.cancelled} deadlineRetries=${result.deadlinesRequeued} deadlines=${result.deadlinesFailed} timers=${result.timersFired} orphanWakes=${result.orphanWakeRepaired} wakeSignals=${result.wakeSignalsSent}`,
        );
      }
      await scheduleNextDueTimer();
      return result;
    } catch (error) {
      logger.warn(`[workflow:v2] reconciler failed: ${workflowV2ErrorMessage(error)}`);
      await scheduleNextDueTimer();
      return EMPTY_WORKFLOW_V2_RECONCILE_RESULT;
    }
  });

  const interval = setIntervalImpl(tick, intervalMs);
  if (typeof (interval as { unref?: () => void }).unref === "function") {
    (interval as { unref: () => void }).unref();
  }

  if (params.wakeSignalBus) {
    const runStartupScan = async () => {
      await tick().catch((error: unknown) => {
        logger.warn(`[workflow:v2] startup scan failed: ${workflowV2ErrorMessage(error)}`);
      });
    };

    void params.wakeSignalBus
      .listen(
        params.wakeSignalKinds ?? ["workflow"],
        async () => {
          await tick();
        },
        { onListen: runStartupScan },
      )
      .then(() => {
        logger.log("[workflow:v2] wake listener registered");
      })
      .catch((error: unknown) => {
        logger.warn(`[workflow:v2] wake listener disabled: ${workflowV2ErrorMessage(error)}`);
      });
  }
}
